import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { login, logout, me as getMe } from '../../api/auth'
import { acceptInvite, getInvite } from '../../api/practice'
import PasswordField from '../../components/PasswordField'
import AuthPage, { AuthField } from '../../components/PublicLayout'
import { parseCompanyId, rememberCompanyId, rememberedCompanyId } from '../../utils/companyId'

// The link in a company's invite email: the accountant accepts with their
// practice (signing in here if they need to), or makes a free practice and
// it's accepted on the way.
export default function InviteAccept({ user, onSignedIn }) {
  const { token } = useParams()
  const nav = useNavigate()
  const [invite, setInvite] = useState(null)
  const [loadErr, setLoadErr] = useState('')
  const [companyId, setCompanyId] = useState(rememberedCompanyId)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    getInvite(token).then(setInvite).catch((e) => setLoadErr(e?.message || 'This invite can’t be found.'))
  }, [token])

  const inPractice = user?.kind === 'practice'
  const accept = async () => {
    setBusy(true); setErr('')
    try {
      const r = await acceptInvite(token)
      nav('/practice', { replace: true, state: { accepted: r?.company_name } })
    } catch (e) {
      setErr(e?.message || 'Couldn’t accept the invite.')
    } finally { setBusy(false) }
  }
  const signInAndAccept = async (e) => {
    e && e.preventDefault()
    const company = parseCompanyId(companyId)
    if (company.error) { setErr(company.error); return }
    if (!username || !password) { setErr('Enter your username and password.'); return }
    setBusy(true); setErr('')
    try {
      await login(username, password, company.id)
      rememberCompanyId(company.id)
      const u = await getMe()
      onSignedIn && onSignedIn(u)
      if (u?.kind !== 'practice') {
        setErr('That’s a company, not a practice. Sign in with your practice’s Company ID, or create a practice below.')
        return
      }
      const r = await acceptInvite(token)
      nav('/practice', { replace: true, state: { accepted: r?.company_name } })
    } catch (e) {
      setErr(e?.message || 'Couldn’t sign in.')
    } finally { setBusy(false) }
  }
  const signOut = async () => {
    try { await logout() } catch { }
    onSignedIn && onSignedIn(null)
  }

  if (loadErr) {
    return (
      <AuthPage title="Invite not found" intro={loadErr} foot={<><Link to="/login">Sign in</Link> · <Link to="/accountants/signup">Create a practice</Link></>}>
        {null}
      </AuthPage>
    )
  }
  if (!invite) return <AuthPage title="Your invite" intro="Loading…">{null}</AuthPage>

  const signupLink = `/accountants/signup?invite=${encodeURIComponent(token)}&email=${encodeURIComponent(invite.email || '')}`
  return (
    <AuthPage
      title={`${invite.company_name} invited you`}
      intro={<>They’d like you to look after their books on ASYS, with <strong>{invite.access_label}</strong> access{invite.access === 'view' ? ' (you can read everything, but not change it)' : ''}. The invite was sent to {invite.email}.</>}
      top={<Link className="pub-btn pub-btn-ghost" to="/login">Sign in</Link>}
    >
      {inPractice ? (
        <div className="pub-form">
          <p className="pub-notice">You’re signed in to your practice. Accept, and {invite.company_name} goes on your Clients list.</p>
          {err && <p className="pub-error" role="alert">{err}</p>}
          <button type="button" className="pub-btn pub-btn-primary pub-btn-block" onClick={accept} disabled={busy}>{busy ? 'Accepting…' : `Accept ${invite.company_name}`}</button>
        </div>
      ) : user ? (
        <div className="pub-form">
          <p className="pub-notice">You’re signed in to a company. Sign out, then sign in to your practice to accept.</p>
          <button type="button" className="pub-btn pub-btn-primary pub-btn-block" onClick={signOut}>Sign out</button>
        </div>
      ) : (
        <form onSubmit={signInAndAccept} className="pub-form" noValidate>
          <p className="pub-group">Already have a practice on ASYS?</p>
          <AuthField id="ia-company" label="Practice’s Company ID" hint="From your practice’s welcome email.">
            <input id="ia-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} inputMode="numeric" autoComplete="organization" />
          </AuthField>
          <AuthField id="ia-username" label="Username">
            <input id="ia-username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
          </AuthField>
          <AuthField id="ia-password" label="Password">
            <PasswordField id="ia-password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </AuthField>
          {err && <p className="pub-error" role="alert">{err}</p>}
          <button type="submit" className="pub-btn pub-btn-primary pub-btn-block" disabled={busy}>{busy ? 'Signing in…' : 'Sign in and accept'}</button>
          <p className="pub-group">New to ASYS?</p>
          <Link className="pub-btn pub-btn-ghost pub-btn-block" to={signupLink}>Create a free practice and accept</Link>
        </form>
      )}
    </AuthPage>
  )
}
